import { z } from "zod";
import { getPlainTextFromDoc } from "../../tiptap/plaintext.js";
import { DiscussionComment, DiscussionListEntry, DiscussionParticipant } from "../discussion.js";
import { FileId } from "../file.js";
import { TipTapDocSchema } from "../tiptapDoc.js";

const MessageInput = z.object({
    contentJson: TipTapDocSchema,
    attachments: z.array(FileId).optional(),
});

export const schema = {
    create: {
        input: z.object({
            title: z
                .string()
                .trim()
                .min(1, "Title must be at least 1 character long")
                .max(100, "Title must be at most 100 characters long"),
            users: z.array(z.number().int().positive()).default([]),
            message: MessageInput.refine(
                (val) =>
                    getPlainTextFromDoc(val.contentJson).trim().length > 0 ||
                    (val.attachments && val.attachments.length > 0),
                {
                    message: "Message must have text or at least one attachment.",
                    path: ["contentJson"],
                },
            ),
        }),
        output: z.object({
            id: z.number(),
        }),
    },
    get: {
        input: z.object({ id: z.number() }),
        output: DiscussionListEntry.extend({
            createdAt: z.string(),
            comments: z.array(DiscussionComment),
        }).nullish(),
    },
    getPageForId: {
        input: z.object({ id: z.number(), pageSize: z.number().default(10) }),
        output: z.object({ page: z.number().optional() }),
    },
    getComment: {
        input: z.object({ id: z.number() }),
        output: DiscussionComment,
    },
    latest: {
        input: z
            .object({
                limit: z.number().lte(10).gte(1).optional().default(10),
            })
            .default({
                limit: 10,
            }),
        output: z.array(
            z.object({
                id: z.number(),
                title: z.string(),
                users: z.array(DiscussionParticipant).nonempty(),
            }),
        ),
    },
    list: {
        input: z.object({
            search: z.coerce.string().min(0).max(100).optional(),
            page: z.coerce.number().int().min(0).optional(),
            pageSize: z.coerce.number().int().min(1).max(30).optional(),
            organizationId: z.coerce.number().int().optional(),
            documentId: z.string().optional(),
            id: z.coerce.number().optional(),
        }),
        output: z.object({
            discussions: z.array(DiscussionListEntry),
            totalFound: z.number(),
            total: z.number(),
        }),
    },
    reply: {
        input: z.object({
            id: z.number(),
            authorId: z.number().optional(),
            message: MessageInput,
        }),
        output: z.void(),
    },
    updateComment: {
        input: z.object({
            commentId: z.number(),
            contentJson: TipTapDocSchema,
            attachments: z.array(FileId).optional(),
        }),
        output: z.void(),
    },

    updateTitle: {
        input: z.object({
            discussionId: z.number(),
            title: z
                .string()
                .trim()
                .min(1, "Title must be at least 1 character long")
                .max(100, "Title must be at most 100 characters long"),
        }),
        output: z.void(),
    },

    addParticipants: {
        input: z.object({
            discussionId: z.number().int().positive(),
            userIds: z.array(z.number().int().positive()).min(1).max(50),
        }),
        output: z.object({ addedUserIds: z.array(z.number()) }),
    },

    removeParticipants: {
        input: z.object({
            discussionId: z.number().int().positive(),
            userIds: z.array(z.number().int().positive()).min(1).max(50),
        }),
        output: z.object({ removedUserIds: z.array(z.number()) }),
    },
};
